import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';
import {
  Users,
  FolderKanban,
  FileWarning,
  CalendarCheck,
  CalendarX,
  Clock,
  TrendingUp,
  UserPlus
} from 'lucide-react';

const COLORS = ['hsl(221.2, 83.2%, 53.3%)', 'hsl(142.1, 76.2%, 36.3%)', 'hsl(47.9, 95.8%, 53.1%)', 'hsl(346.8, 77.2%, 49.8%)', 'hsl(199, 89%, 48%)'];

export const Dashboard = () => {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);
  const [leavesData, setLeavesData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, attendanceRes, leavesRes] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/dashboard/charts/attendance'),
          api.get('/dashboard/charts/leaves')
        ]);
        setStats(statsRes?.data && typeof statsRes.data === "object" ? statsRes.data : null);
        setAttendanceData(Array.isArray(attendanceRes?.data) ? attendanceRes.data : []);
        setLeavesData(Array.isArray(leavesRes?.data) ? leavesRes.data : []);

      } catch (error) {
        toast.error('Failed to load dashboard data');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api]);

  const StatCard = ({ title, value, icon: Icon, description, color = 'primary' }) => (
    <Card className="card-hover" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className="text-3xl font-bold mt-2 tracking-tight">{value}</p>
            {description && (
              <p className="text-sm text-muted-foreground mt-1">{description}</p>
            )}
          </div>
          <div className={`p-3 rounded-xl bg-${color}/10`}>
            <Icon className={`w-6 h-6 text-${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="page-transition space-y-6">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-transition space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Overview of your HR management system</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Personnel"
          value={stats?.total_personnel || 0}
          icon={Users}
          description="Active employees"
        />
        <StatCard
          title="Categories"
          value={stats?.total_categories || 0}
          icon={FolderKanban}
          description="Workflow stages"
        />
        <StatCard
          title="Pending Documents"
          value={stats?.pending_documents || 0}
          icon={FileWarning}
          description="Unassigned personnel"
        />
        <StatCard
          title="Pending Leaves"
          value={stats?.pending_leaves || 0}
          icon={Clock}
          description="Awaiting approval"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attendance Chart */}
        <Card data-testid="attendance-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarCheck className="w-5 h-5 text-primary" />
              Monthly Attendance
            </CardTitle>
            <CardDescription>Present vs Absent days this year</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={attendanceData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12 }} 
                    className="text-muted-foreground"
                  />
                  <YAxis tick={{ fontSize: 12 }} className="text-muted-foreground" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="present" name="Present" fill="hsl(142.1, 76.2%, 36.3%)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="absent" name="Absent" fill="hsl(346.8, 77.2%, 49.8%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Category Distribution */}
        <Card data-testid="category-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FolderKanban className="w-5 h-5 text-primary" />
              Personnel by Category
            </CardTitle>
            <CardDescription>Distribution across workflow stages</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              {stats?.category_stats && stats.category_stats.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.category_stats}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="count"
                      nameKey="name"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {stats.category_stats.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  No category data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Leave Types */}
        <Card data-testid="leaves-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarX className="w-5 h-5 text-primary" />
              Leave Summary
            </CardTitle>
            <CardDescription>Days taken by type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={leavesData} layout="vertical" margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="type" type="category" tick={{ fontSize: 12 }} width={60} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="days" name="Days" fill="hsl(221.2, 83.2%, 53.3%)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Recent Personnel */}
        <Card className="lg:col-span-2" data-testid="recent-personnel">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              Recent Personnel
            </CardTitle>
            <CardDescription>Latest additions to the system</CardDescription>
          </CardHeader>
          <CardContent>
            {stats?.recent_personnel && stats.recent_personnel.length > 0 ? (
              <div className="space-y-4">
                {stats.recent_personnel.map((person, index) => (
                  <div
                    key={person.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                    data-testid={`recent-person-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-primary font-semibold">
                          {person.name.charAt(0)}{person.surname.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{person.name} {person.surname}</p>
                        <p className="text-sm text-muted-foreground">{person.position || 'No position'}</p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {person.department || 'No dept'}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No recent personnel
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Attendance Summary */}
      <Card data-testid="attendance-summary">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-500/10">
                <TrendingUp className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">This Month's Attendance</p>
                <p className="text-2xl font-bold">
                  {stats?.attendance?.total_present || 0} Present / {stats?.attendance?.total_absent || 0} Absent
                </p>
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              {stats?.attendance?.month}/{stats?.attendance?.year}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
